import {
  prepareEvidenceImage,
  decodeEvidenceImage,
  type EvidenceClass,
  type MediaPreparationProfile,
  type MediaPurpose,
  type MediaFeatureOwner,
  type ScopedUploadManifest,
} from '@kop/media';
import type {
  CommittedMediaDownloadDescriptor,
  CommittedMediaDownloadRequest,
  CommittedMediaManifest,
  MediaUploadInput,
} from '@kop/media-firebase-adapter';
import { firebaseEmulatorEndpoint } from '../../runtime/firebase-emulator-endpoint';
import { normalizeAlbumPhoto } from './album-photo';

type Env = Readonly<Record<string, string | boolean | undefined>>;
type TrustedDisplayAsset = { uri: string; mimeType: string; sha256: string; revoke: () => void };
export type TrustedMediaScope = {
  tenantId: string;
  siteId: string;
  contractorId: string | null;
};
export type TrustedMediaStatus =
  | { state: 'preparing' }
  | { state: 'queued'; attempt: number; nextAttemptAt?: string }
  | { state: 'uploading'; progress: number }
  | { state: 'committed'; manifest: ScopedUploadManifest }
  | { state: 'failed'; message: string; attempt: number; nextAttemptAt?: string };
export type TrustedMediaReference = {
  manifestId: string;
  sourceSha256: string;
  thumbnailVariantSha256?: string;
  evidenceVariantSha256: string;
  evidenceBytes?: number;
  evidenceMimeType?: string;
};

type QueueItem = {
  manifestId: string;
  evidenceId: string;
  purpose: MediaPurpose;
  featureOwner?: MediaFeatureOwner;
  evidenceClass: EvidenceClass;
  mediaProfile?: MediaPreparationProfile;
  scope: TrustedMediaScope;
  file: Blob;
  fileName: string;
  attempt: number;
  nextAttemptAt?: string;
  ownerUid: string;
  createdAt: string;
  expiresAt: string;
};

interface NetworkAdapter {
  authorizeCommittedDisplayBatch?(
    tenantId: string,
    references: readonly CommittedMediaDownloadRequest[],
    siteId?: string,
  ): Promise<readonly (CommittedMediaDownloadDescriptor | null)[]>;
  currentUserId(): string | null;
  upload(
    input: MediaUploadInput,
    onProgress?: (ratio: number) => void,
  ): Promise<ScopedUploadManifest>;
  getManifest(
    tenantId: string,
    reference: TrustedMediaReference,
  ): Promise<CommittedMediaManifest | null>;
  resolveCommittedVariant(
    tenantId: string,
    manifestId: string,
    expectedSha256: string,
    variant?: 'thumbnail' | 'preview' | 'evidence',
    siteId?: string,
  ): Promise<{ uri: string; mimeType: string; sha256: string; revoke: () => void }>;
  authorizeCommittedDisplayVariants(
    tenantId: string,
    references: readonly CommittedMediaDownloadRequest[],
    siteId?: string,
  ): Promise<readonly CommittedMediaDownloadDescriptor[]>;
}

export interface TrustedMediaPort {
  readonly enabled: boolean;
  upload(
    file: File | Blob,
    input: {
      fileName: string;
      evidenceId: string;
      purpose: MediaPurpose;
      featureOwner?: MediaFeatureOwner;
      evidenceClass: EvidenceClass;
      mediaProfile?: MediaPreparationProfile;
      scope: TrustedMediaScope;
      ownerUid: string;
      manifestId?: string;
    },
    onStatus?: (status: TrustedMediaStatus) => void,
  ): Promise<TrustedMediaReference>;
  hydrate(
    tenantId: string,
    reference: TrustedMediaReference,
  ): Promise<CommittedMediaManifest | null>;
  resolvePrintAsset(
    tenantId: string,
    reference: TrustedMediaReference,
  ): Promise<{ uri: string; mimeType: string; sha256: string; revoke: () => void }>;
  resolveAuthoritativePrintAsset(
    tenantId: string,
    reference: {
      manifestId: string;
      evidenceVariantSha256: string;
      variant?: 'thumbnail' | 'evidence';
      siteId?: string;
    },
  ): Promise<{ uri: string; mimeType: string; sha256: string; revoke: () => void }>;
  resolveAuthoritativeDisplayAsset(
    tenantId: string,
    reference: {
      manifestId: string;
      evidenceVariantSha256: string;
      variant?: 'thumbnail' | 'evidence';
      siteId?: string;
      inspectionFindingId?: string;
    },
  ): Promise<{ uri: string; mimeType: string; sha256: string; revoke: () => void }>;
  flush(
    ownerUid: string,
    onStatus?: (manifestId: string, status: TrustedMediaStatus) => void,
  ): Promise<void>;
  purge(ownerUid?: string): Promise<void>;
}

const delayFor = (attempt: number) => Math.min(5 * 60_000, 1_000 * 2 ** Math.min(attempt, 8));
const MAX_FILE_BYTES = 15 * 1024 * 1024;
const MAX_AERIAL_SOURCE_BYTES = 50 * 1024 * 1024;
const MAX_QUEUE_BYTES = 50 * 1024 * 1024;
const MAX_QUEUE_ITEMS = 12;
const QUEUE_TTL_MS = 24 * 60 * 60 * 1000;
const AERIAL_MAX_LONG_EDGE = 2048;
const AERIAL_QUALITIES = [0.86, 0.78, 0.7, 0.64, 0.58] as const;
const allowedMime = new Set(['image/jpeg', 'image/png', 'image/webp']);

export async function preflightMediaSource(file: Blob, mediaProfile?: MediaPreparationProfile) {
  const sourceLimit = mediaProfile === 'site-aerial-map' ? MAX_AERIAL_SOURCE_BYTES : MAX_FILE_BYTES;
  if (!allowedMime.has(file.type) || file.size <= 0 || file.size > sourceLimit)
    throw new Error('MEDIA_PREFLIGHT_REJECTED');
  const bitmap = await decodeEvidenceImage(file);
  try {
    if (
      bitmap.width <= 0 ||
      bitmap.height <= 0 ||
      bitmap.width > 12_000 ||
      bitmap.height > 12_000 ||
      bitmap.width * bitmap.height > 60_000_000
    )
      throw new Error('MEDIA_PIXEL_LIMIT_EXCEEDED');
  } finally {
    bitmap.close?.();
  }
}

function canvasBlob(canvas: HTMLCanvasElement, type: string, quality: number) {
  return new Promise<Blob>((resolve, reject) =>
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('IMAGE_ENCODE_FAILED'))),
      type,
      quality,
    ),
  );
}

/**
 * The aerial-map profile preserves its prepared source for immutable review. Normalize that
 * source before manifest creation so an oversized camera original never reaches Storage.
 */
export async function normalizeAerialMapSource(file: Blob): Promise<Blob> {
  const bitmap = await decodeEvidenceImage(file);
  try {
    const scale = Math.min(1, AERIAL_MAX_LONG_EDGE / Math.max(bitmap.width, bitmap.height));
    const width = Math.max(1, Math.round(bitmap.width * scale));
    const height = Math.max(1, Math.round(bitmap.height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    if (!context) throw new Error('CANVAS_CONTEXT_UNAVAILABLE');
    context.drawImage(bitmap.source, 0, 0, width, height);
    const webpProbe = await canvasBlob(canvas, 'image/webp', 0.5);
    const format = webpProbe.type === 'image/webp' ? 'image/webp' : 'image/jpeg';
    for (const quality of AERIAL_QUALITIES) {
      const normalized = await canvasBlob(canvas, format, quality);
      if (normalized.size > 0 && normalized.size <= MAX_FILE_BYTES) return normalized;
    }
    throw new Error('AERIAL_MAP_NORMALIZATION_SIZE_LIMIT');
  } finally {
    bitmap.close?.();
  }
}

/** Local recovery storage is optional for an online upload, never the commit authority. */
class Queue {
  private readonly memory = new Map<string, QueueItem>();
  private mutation: Promise<unknown> = Promise.resolve();
  private open() {
    return new Promise<IDBDatabase>((resolve, reject) => {
      if (typeof indexedDB === 'undefined') {
        reject(new DOMException('Local media storage unavailable', 'InvalidStateError'));
        return;
      }
      let settled = false;
      const fail = (error: unknown) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        reject(error ?? new DOMException('Local media storage unavailable', 'UnknownError'));
      };
      const timer = setTimeout(
        () => fail(new DOMException('Local media storage timed out', 'TimeoutError')),
        4_000,
      );
      try {
        const request = indexedDB.open('kop-trusted-media-v2', 1);
        request.onupgradeneeded = () => {
          if (!request.result.objectStoreNames.contains('uploads'))
            request.result.createObjectStore('uploads', { keyPath: 'manifestId' });
        };
        request.onsuccess = () => {
          if (settled) {
            request.result.close();
            return;
          }
          settled = true;
          clearTimeout(timer);
          resolve(request.result);
        };
        request.onerror = () => fail(request.error);
        request.onblocked = () =>
          fail(new DOMException('Local media storage blocked', 'InvalidStateError'));
      } catch (error) {
        fail(error);
      }
    });
  }
  async put(value: QueueItem) {
    const operation = this.mutation.catch(() => undefined).then(() => this.putNow(value));
    this.mutation = operation.catch(() => undefined);
    return operation;
  }
  private async putNow(value: QueueItem) {
    let db: IDBDatabase | undefined;
    try {
      db = await this.open();
      const rows = await this.listFrom(db);
      const expired = rows.filter((item) => Date.parse(item.expiresAt) <= Date.now());
      if (expired.length)
        await transaction(db, 'readwrite', (store) => {
          for (const item of expired) store.delete(item.manifestId);
        });
      const others = rows.filter(
        (item) => item.manifestId !== value.manifestId && !expired.includes(item),
      );
      if (
        others.length + 1 > MAX_QUEUE_ITEMS ||
        others.reduce((sum, item) => sum + item.file.size, 0) + value.file.size > MAX_QUEUE_BYTES
      )
        throw new DOMException('Local media queue full', 'QuotaExceededError');
      await transaction(db, 'readwrite', (store) => store.put(value));
      this.memory.delete(value.manifestId);
    } catch (error) {
      if (!(error instanceof DOMException)) throw error;
      // Retain existing queued files. The current in-flight file can still reach the server.
      for (const [key, item] of this.memory)
        if (Date.parse(item.expiresAt) <= Date.now()) this.memory.delete(key);
      const others = [...this.memory.values()].filter(
        (item) => item.manifestId !== value.manifestId,
      );
      if (
        others.length + 1 > MAX_QUEUE_ITEMS ||
        others.reduce((sum, item) => sum + item.file.size, 0) + value.file.size > MAX_QUEUE_BYTES
      )
        throw new Error('MEDIA_OFFLINE_QUEUE_QUOTA_EXCEEDED');
      this.memory.set(value.manifestId, value);
    } finally {
      db?.close();
    }
  }
  async remove(id: string) {
    this.memory.delete(id);
    let db: IDBDatabase | undefined;
    try {
      db = await this.open();
      await transaction(db, 'readwrite', (store) => store.delete(id));
    } catch (error) {
      // A failed cleanup cannot turn a verified server commit into a failed registration.
      if (!(error instanceof DOMException)) throw error;
    } finally {
      db?.close();
    }
  }
  async list(): Promise<QueueItem[]> {
    let db: IDBDatabase | undefined;
    try {
      db = await this.open();
      return [
        ...new Map(
          [...(await this.listFrom(db)), ...this.memory.values()].map((row) => [
            row.manifestId,
            row,
          ]),
        ).values(),
      ];
    } catch (error) {
      if (!(error instanceof DOMException)) throw error;
      return [...this.memory.values()];
    } finally {
      db?.close();
    }
  }
  private listFrom(db: IDBDatabase): Promise<QueueItem[]> {
    return new Promise((resolve, reject) => {
      const tx = db.transaction('uploads'),
        request = tx.objectStore('uploads').getAll();
      request.onsuccess = () => resolve(request.result as QueueItem[]);
      request.onerror = () =>
        reject(request.error ?? new DOMException('Local media read failed', 'UnknownError'));
      tx.onabort = () =>
        reject(tx.error ?? new DOMException('Local media read aborted', 'AbortError'));
    });
  }
  async purge(ownerUid?: string) {
    const rows = await this.list();
    for (const row of rows)
      if (!ownerUid || row.ownerUid === ownerUid) await this.remove(row.manifestId);
  }
}
function transaction(
  db: IDBDatabase,
  mode: IDBTransactionMode,
  operation: (store: IDBObjectStore) => unknown,
) {
  return new Promise<void>((resolve, reject) => {
    const tx = db.transaction('uploads', mode);
    tx.oncomplete = () => resolve();
    tx.onerror = (event) =>
      reject(
        tx.error ??
          (event.target as IDBRequest)?.error ??
          new DOMException('Local media write failed', 'UnknownError'),
      );
    tx.onabort = () =>
      reject(tx.error ?? new DOMException('Local media write aborted', 'AbortError'));
    try {
      operation(tx.objectStore('uploads'));
    } catch (error) {
      tx.abort();
      reject(error);
    }
  });
}
class DisabledMediaPort implements TrustedMediaPort {
  readonly enabled = false;
  async upload(): Promise<never> {
    throw new Error('사진 첨부 서버가 연결되지 않아 파일 정보만 임시로 저장했습니다.');
  }
  async hydrate(): Promise<null> {
    return null;
  }
  async resolvePrintAsset(): Promise<never> {
    throw new Error('COMMITTED_MEDIA_REQUIRED');
  }
  async resolveAuthoritativePrintAsset(): Promise<never> {
    throw new Error('COMMITTED_MEDIA_REQUIRED');
  }
  async resolveAuthoritativeDisplayAsset(): Promise<never> {
    throw new Error('COMMITTED_MEDIA_REQUIRED');
  }
  async flush(): Promise<void> {}
  async purge(): Promise<void> {}
}

export class EmulatorMediaPort implements TrustedMediaPort {
  readonly enabled = true;
  private readonly queue = new Queue();
  private networkPromise: Promise<NetworkAdapter> | null = null;
  private displayCacheOwner: string | null = null;
  private readonly displayCache = new Map<
    string,
    {
      asset: { uri: string; mimeType: string; sha256: string; revoke: () => void };
      expiresAt: number;
    }
  >();
  private readonly pendingDisplays = new Map<
    string,
    {
      tenantId: string;
      siteId: string;
      ownerUid: string;
      request: CommittedMediaDownloadRequest;
      resolve(value: { uri: string; mimeType: string; sha256: string; revoke: () => void }): void;
      reject(cause: unknown): void;
    }[]
  >();
  private displayFlushScheduled = false;
  private readonly displayInFlight = new Map<string, Promise<TrustedDisplayAsset>>();

  constructor(private readonly networkFactory: () => Promise<NetworkAdapter>) {}

  private network() {
    this.networkPromise ??= this.networkFactory();
    return this.networkPromise;
  }
  private displayKey(tenantId: string, siteId: string, request: CommittedMediaDownloadRequest) {
    return `${tenantId}:${siteId}:${request.inspectionFindingId ?? ''}:${request.manifestId}:${request.expectedSha256}:${request.variant}`;
  }
  private clearDisplayCache() {
    this.displayCache.clear();
    this.displayInFlight.clear();
    this.displayCacheOwner = null;
    const error = new Error('MEDIA_SESSION_CHANGED');
    for (const waiters of this.pendingDisplays.values())
      for (const waiter of waiters) waiter.reject(error);
    this.pendingDisplays.clear();
  }
  private scheduleDisplayFlush(network: NetworkAdapter) {
    if (this.displayFlushScheduled) return;
    this.displayFlushScheduled = true;
    queueMicrotask(() => void this.flushDisplayBatch(network));
  }
  private async flushDisplayBatch(network: NetworkAdapter) {
    this.displayFlushScheduled = false;
    const first = this.pendingDisplays.values().next().value?.[0];
    const rows = [...this.pendingDisplays.entries()]
      .filter(
        ([, waiters]) =>
          waiters[0]?.tenantId === first?.tenantId &&
          waiters[0]?.siteId === first?.siteId &&
          waiters[0]?.ownerUid === first?.ownerUid,
      )
      .slice(0, 50);
    for (const [key] of rows) this.pendingDisplays.delete(key);
    if (!rows.length) return;
    const tenantId = rows[0]![1][0]!.tenantId;
    const siteId = rows[0]![1][0]!.siteId;
    const ownerUid = rows[0]![1][0]!.ownerUid;
    if (
      rows.some(
        ([, waiters]) => waiters[0]!.tenantId !== tenantId || waiters[0]!.ownerUid !== ownerUid,
      )
    ) {
      const error = new Error('MEDIA_BATCH_SCOPE_MISMATCH');
      for (const [, waiters] of rows) for (const waiter of waiters) waiter.reject(error);
      if (this.pendingDisplays.size) this.scheduleDisplayFlush(network);
      return;
    }
    try {
      const requests = rows.map(([, waiters]) => waiters[0]!.request);
      const descriptors = network.authorizeCommittedDisplayBatch
        ? await network.authorizeCommittedDisplayBatch(tenantId, requests, siteId)
        : await network.authorizeCommittedDisplayVariants(tenantId, requests, siteId);
      if (network.currentUserId() !== ownerUid || this.displayCacheOwner !== ownerUid)
        throw new Error('MEDIA_SESSION_CHANGED');
      if (descriptors.length !== rows.length) throw new Error('INVALID_MEDIA_DOWNLOAD_RESPONSE');
      const now = Date.now();
      const validated = rows.map(([key, waiters], index) => {
        const descriptor = descriptors[index];
        if (descriptor === null) return { key, waiters, unavailable: true as const };
        if (!descriptor) throw new Error('INVALID_MEDIA_DOWNLOAD_RESPONSE');
        const request = waiters[0]!.request;
        if (
          descriptor.manifestId !== request.manifestId ||
          descriptor.sha256 !== request.expectedSha256 ||
          descriptor.variant !== request.variant
        )
          throw new Error('INVALID_MEDIA_DOWNLOAD_RESPONSE');
        const serverExpiry = Date.parse(descriptor.expiresAt);
        if (!Number.isFinite(serverExpiry) || serverExpiry <= now + 15_000)
          throw new Error('MEDIA_DOWNLOAD_DESCRIPTOR_EXPIRED');
        return {
          key,
          waiters,
          serverExpiry,
          descriptor,
          asset: {
            uri: descriptor.url,
            mimeType: descriptor.mimeType,
            sha256: descriptor.sha256,
            // A signed URL is shared by list/detail/zoom. One view must never revoke it for others.
            revoke: () => undefined,
          },
        };
      });
      validated.forEach((result) => {
        const { key, waiters } = result;
        if ('unavailable' in result) {
          for (const waiter of waiters) waiter.reject(new Error('MEDIA_EVIDENCE_UNAVAILABLE'));
          return;
        }
        const { serverExpiry, asset } = result;
        this.displayCache.set(key, {
          asset,
          expiresAt: Math.min(serverExpiry - 15_000, now + 90_000),
        });
        for (const waiter of waiters) waiter.resolve(asset);
      });
    } catch (cause) {
      for (const [, waiters] of rows) for (const waiter of waiters) waiter.reject(cause);
    } finally {
      if (this.pendingDisplays.size) this.scheduleDisplayFlush(network);
    }
  }
  async upload(
    file: File | Blob,
    input: Parameters<TrustedMediaPort['upload']>[1],
    onStatus?: (status: TrustedMediaStatus) => void,
  ) {
    if (!/^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/u.test(input.scope.siteId))
      throw new Error('MEDIA_SCOPE_REQUIRED');
    if (!input.ownerUid || (await this.network()).currentUserId() !== input.ownerUid)
      throw new Error('MEDIA_SESSION_OWNER_MISMATCH');
    onStatus?.({ state: 'preparing' });
    // Ordinary field photos can be converted before immutable manifest creation. Classes
    // requiring original preservation never silently replace the selected original.
    const source = await normalizeAlbumPhoto(
      file,
      input.evidenceClass === 'routine_inspection' || input.evidenceClass === 'corrective_action',
    );
    await preflightMediaSource(source, input.mediaProfile);
    const manifestId = input.manifestId ?? `media-${crypto.randomUUID()}`;
    const createdAt = new Date().toISOString();
    const queued: QueueItem = {
      ...input,
      manifestId,
      file: source,
      attempt: 0,
      createdAt,
      expiresAt: new Date(Date.parse(createdAt) + QUEUE_TTL_MS).toISOString(),
    };
    await this.queue.put(queued);
    onStatus?.({ state: 'queued', attempt: 0 });
    return this.process(queued, onStatus);
  }
  private async process(item: QueueItem, onStatus?: (status: TrustedMediaStatus) => void) {
    try {
      if (Date.parse(item.expiresAt) <= Date.now()) {
        await this.queue.remove(item.manifestId);
        throw new Error('MEDIA_OFFLINE_QUEUE_EXPIRED');
      }
      if ((await this.network()).currentUserId() !== item.ownerUid)
        throw new Error('MEDIA_SESSION_OWNER_MISMATCH');
      onStatus?.({ state: 'preparing' });
      const source =
        item.mediaProfile === 'site-aerial-map'
          ? await normalizeAerialMapSource(item.file)
          : item.file;
      const prepared = await prepareEvidenceImage(source, item.evidenceClass, {
        profile: item.mediaProfile ?? 'default',
      });
      const manifest = await (
        await this.network()
      ).upload(
        {
          ...item.scope,
          manifestId: item.manifestId,
          evidenceId: item.evidenceId,
          purpose: item.purpose,
          featureOwner: item.featureOwner,
          evidenceClass: item.evidenceClass,
          mediaProfile: item.mediaProfile ?? 'default',
          prepared,
        },
        (progress) => onStatus?.({ state: 'uploading', progress }),
      );
      if (
        manifest.status !== 'committed' ||
        manifest.siteId !== item.scope.siteId ||
        manifest.tenantId !== item.scope.tenantId ||
        manifest.contractorId !== item.scope.contractorId
      )
        throw new Error('MEDIA_COMMIT_NOT_CONFIRMED');
      const evidence = manifest.parts.find(
        (part) => part.variant === 'evidence' && part.state === 'verified',
      );
      const thumbnail = manifest.parts.find(
        (part) => part.variant === 'thumbnail' && part.state === 'verified',
      );
      if (!evidence) throw new Error('VERIFIED_EVIDENCE_VARIANT_REQUIRED');
      await this.queue.remove(item.manifestId);
      onStatus?.({ state: 'committed', manifest });
      return {
        manifestId: manifest.id,
        sourceSha256: manifest.sourceSha256,
        ...(thumbnail ? { thumbnailVariantSha256: thumbnail.sha256 } : {}),
        evidenceVariantSha256: evidence.sha256,
        evidenceBytes: evidence.bytes,
        evidenceMimeType: evidence.mimeType,
      };
    } catch (error) {
      const attempt = item.attempt + 1;
      const nextAttemptAt = new Date(Date.now() + delayFor(attempt)).toISOString();
      await this.queue.put({ ...item, attempt, nextAttemptAt });
      const message = error instanceof Error ? error.message : 'MEDIA_UPLOAD_FAILED';
      onStatus?.({ state: 'failed', message, attempt, nextAttemptAt });
      throw error;
    }
  }
  async hydrate(tenantId: string, reference: TrustedMediaReference) {
    const manifest = await (await this.network()).getManifest(tenantId, reference);
    if (
      !manifest ||
      manifest.status !== 'committed' ||
      manifest.sourceSha256 !== reference.sourceSha256
    )
      return null;
    return manifest.parts.some(
      (part) =>
        part.variant === 'evidence' &&
        part.state === 'verified' &&
        part.sha256 === reference.evidenceVariantSha256,
    )
      ? manifest
      : null;
  }
  async resolvePrintAsset(tenantId: string, reference: TrustedMediaReference) {
    const manifest = await this.hydrate(tenantId, reference);
    if (!manifest?.siteId) throw new Error('COMMITTED_MEDIA_REQUIRED');
    return (await this.network()).resolveCommittedVariant(
      tenantId,
      reference.manifestId,
      reference.evidenceVariantSha256,
      'evidence',
      manifest.siteId,
    );
  }
  async resolveAuthoritativePrintAsset(
    tenantId: string,
    reference: {
      manifestId: string;
      evidenceVariantSha256: string;
      variant?: 'thumbnail' | 'evidence';
      siteId?: string;
    },
  ) {
    return (await this.network()).resolveCommittedVariant(
      tenantId,
      reference.manifestId,
      reference.evidenceVariantSha256,
      reference.variant ?? 'evidence',
      reference.siteId ?? 'site-eumseong',
    );
  }
  async resolveAuthoritativeDisplayAsset(
    tenantId: string,
    reference: {
      manifestId: string;
      evidenceVariantSha256: string;
      variant?: 'thumbnail' | 'evidence';
      siteId?: string;
      inspectionFindingId?: string;
    },
  ) {
    if (tenantId !== 'kop') throw new Error('EUMSEONG_MEDIA_ONLY');
    const network = await this.network();
    const owner = network.currentUserId();
    if (!owner) {
      this.clearDisplayCache();
      throw new Error('Authenticated media session is required');
    }
    if (this.displayCacheOwner !== owner) {
      this.clearDisplayCache();
      this.displayCacheOwner = owner;
    }
    const request: CommittedMediaDownloadRequest = {
      manifestId: reference.manifestId,
      expectedSha256: reference.evidenceVariantSha256,
      variant: reference.variant ?? 'evidence',
      ...(reference.inspectionFindingId
        ? { inspectionFindingId: reference.inspectionFindingId }
        : {}),
    };
    const siteId = reference.siteId ?? 'site-eumseong';
    const key = this.displayKey(tenantId, siteId, request);
    const cached = this.displayCache.get(key);
    if (cached && cached.expiresAt > Date.now()) return cached.asset;
    this.displayCache.delete(key);
    const existing = this.displayInFlight.get(key);
    if (existing) return existing;
    const flight = new Promise<TrustedDisplayAsset>((resolve, reject) => {
      const waiter = { tenantId, siteId, ownerUid: owner, request, resolve, reject };
      const pending = this.pendingDisplays.get(key);
      if (pending) pending.push(waiter);
      else this.pendingDisplays.set(key, [waiter]);
      this.scheduleDisplayFlush(network);
    }).finally(() => {
      if (this.displayInFlight.get(key) === flight) this.displayInFlight.delete(key);
    });
    this.displayInFlight.set(key, flight);
    return flight;
  }
  async flush(
    ownerUid: string,
    onStatus?: (manifestId: string, status: TrustedMediaStatus) => void,
  ) {
    if (!navigator.onLine) return;
    for (const item of await this.queue.list()) {
      if (Date.parse(item.expiresAt) <= Date.now()) {
        await this.queue.remove(item.manifestId);
        continue;
      }
      if (item.ownerUid !== ownerUid || (await this.network()).currentUserId() !== ownerUid)
        continue;
      if (item.nextAttemptAt && Date.parse(item.nextAttemptAt) > Date.now()) continue;
      try {
        await this.process(item, (status) => onStatus?.(item.manifestId, status));
      } catch {
        /* retained for backoff */
      }
    }
  }
  async purge(ownerUid?: string) {
    this.clearDisplayCache();
    await this.queue.purge(ownerUid);
  }
}

export function createTrustedMediaAdapter(env: Env = import.meta.env): TrustedMediaPort {
  const mode =
    env.VITE_MEDIA_DATA_ADAPTER ?? (env.VITE_AUTH_ADAPTER === 'firebase' ? 'firebase' : undefined);
  if (mode !== 'firebase-emulator' && mode !== 'firebase') return new DisabledMediaPort();
  const projectId = String(env.VITE_FIREBASE_PROJECT_ID ?? '');
  if (mode === 'firebase') {
    if (projectId !== 'kop-safety-51efb')
      throw new Error('Media production adapter requires the corporate Firebase project');
    return new EmulatorMediaPort(() =>
      import('@kop/media-firebase-adapter').then((module) =>
        module.createMediaFirebaseAdapter(projectId),
      ),
    );
  }
  if (!projectId.startsWith('demo-')) throw new Error('Media emulator requires demo-* project');
  const functions = firebaseEmulatorEndpoint(
    env.VITE_FUNCTIONS_EMULATOR_HOST,
    15001,
    'Media adapter accepts loopback only',
  );
  const storage = firebaseEmulatorEndpoint(
    env.VITE_STORAGE_EMULATOR_HOST,
    19199,
    'Media adapter accepts loopback only',
  );
  const firestore = firebaseEmulatorEndpoint(
    env.VITE_FIRESTORE_EMULATOR_HOST,
    18080,
    'Media adapter accepts loopback only',
  );
  return new EmulatorMediaPort(() =>
    import('@kop/media-firebase-adapter').then((module) =>
      module.createMediaFirebaseEmulatorAdapter({
        projectId: projectId as `demo-${string}`,
        functions,
        storage,
        firestore,
      }),
    ),
  );
}
// Constructed on first use, never at module evaluation. See getInspectionDataAdapter.
let trustedMediaAdapterHolder: { value: TrustedMediaPort } | null = null;

/** The process-wide trusted media adapter. Selection and its guards run once, on first access. */
export function getTrustedMediaAdapter(): TrustedMediaPort {
  trustedMediaAdapterHolder ??= { value: createTrustedMediaAdapter() };
  return trustedMediaAdapterHolder.value;
}
